import { COLOR_PALETTE, CHART_STYLES } from './chart.constants';
import { 
  formatPercent,
  chartFormatter,
  getCategoryAxis,
  getValueAxis,
  seriesColor,
  lineDash,
  toRelativeSeries,
  getTooltip,
  getLegend,
} from './chartHelpers';

const baseFrame = ({ overrides = {}, showLegend = true } = {}) => ({
  backgroundColor: 'transparent',
  color: COLOR_PALETTE,
  legend: showLegend ? getLegend() : { show: false },
  grid: {
    left: 4,
    right: 8,
    top: 36,
    bottom: 0,
    containLabel: true,
  },
  ...overrides,
});

export const buildLineOption = ({ categories = [], series = [] } = {}) => ({
  ...baseFrame(),
  tooltip: getTooltip(),
  xAxis: getCategoryAxis(categories, false),
  yAxis: getValueAxis(),
  series: series.map((item, index) => {
    const color = seriesColor(item, index);
    return {
      type: 'line',
      name: item.name,
      data: item.values,
      smooth: true,
      symbol: 'circle',
      symbolSize: 7,
      itemStyle: { color },
      lineStyle: { color, width: 2, ...lineDash(item) },
    };
  }),
});

// For progress/cost tracking charts
export const buildSummaryColumnOption = ({ categories = [], series = [] } = {}) => ({
  ...baseFrame({ 
    overrides: {
      grid: {
        left: 4,
        right: 8,
        top: 12,
        bottom: 2,
        containLabel: true,
      }
    },
    showLegend: false 
  }),
  tooltip: getTooltip(),
  xAxis: getCategoryAxis(categories, true),
  yAxis: getValueAxis(),
  series: series.map((item, index) => ({
    type: 'bar',
    name: item.name,
    data: item.values,
    barMaxWidth: 48,
    itemStyle: {
      color: seriesColor(item, index),
      borderRadius: [4, 4, 0, 0],
    },
  })),
});

export const buildColumnOption = ({ categories = [], series = [] } = {}) => ({
  ...baseFrame(),
  tooltip: getTooltip({
    axisPointer: { type: 'shadow', shadowStyle: { color: 'rgba(203, 213, 225, 0.45)' } },
  }),
  xAxis: getCategoryAxis(categories, true),
  yAxis: getValueAxis(),
  series: series.map((item, index) => ({
    type: 'bar',
    name: item.name,
    data: item.values,
    barMaxWidth: 48,
    itemStyle: {
      color: seriesColor(item, index),
      borderRadius: [4, 4, 0, 0],
    },
  })),
});

export const buildPieOption = ({ slices = [] } = {}) => ({
  ...baseFrame(),
  tooltip: getTooltip({ trigger: 'item', formatter: chartFormatter }),
  series: [
    {
      type: 'pie',
      top: 36,
      radius: ['55%', '80%'], // donut chart (inner and outer radius)
      center: ['50%', '50%'],
      startAngle: 90,
      label: { show: false },
      labelLine: { show: false },
      itemStyle: {
        borderColor: '#fff',
        borderWidth: 4,
        borderRadius: 8,
      },
      data: slices.map((slice) => ({
        name: slice.name,
        value: slice.value,
        ...(slice.color ? { itemStyle: { color: slice.color } } : {}),
      })),
    },
  ],
});

export const buildStackedSteppedOption = ({ categories = [], series = [] } = {}) => {
  const relativeSeries = toRelativeSeries(series, categories.length);
  return {
    ...baseFrame(),
    tooltip: getTooltip(),
    xAxis: getCategoryAxis(categories, false),
    yAxis: getValueAxis({
      max: 1,
      axisLabel: {
        color: CHART_STYLES.LABEL_COLOR,
        fontSize: 12,
        formatter: formatPercent,
      },
    }),
    series: relativeSeries.map((item, index) => {
      const color = seriesColor(item, index);
      return {
        type: 'line',
        name: item.name,
        data: item.points,
        step: 'middle',
        stack: 'total',
        symbol: 'none',
        itemStyle: { color },
        lineStyle: item.dashed
          ? { color: CHART_STYLES.TEXT_COLOR, width: 2, type: 'dashed' }
          : { color, width: 0 },
        areaStyle: { color, opacity: 1 },
      };
    }),
  };
};
